import { useState } from 'react'
import { supabase } from './supabase'

export default function Signup({ onSuccess, onLogin }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    username: '', phone: '', password: '', confirm: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value })

  const handleSignup = async () => {
    setError(null)

    // Basic validation
    if (!form.firstName || !form.lastName || !form.email || !form.username || !form.phone || !form.password) {
      return setError('Please fill in all fields.')
    }
    if (form.password !== form.confirm) {
      return setError('Passwords do not match.')
    }
    if (form.password.length < 8) {
      return setError('Password must be at least 8 characters.')
    }
    if (!form.username.match(/^[a-zA-Z0-9_]+$/)) {
      return setError('Username can only contain letters, numbers, and underscores.')
    }

    setLoading(true)

    // Create auth user
    const { data, error: signupError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
    })

    if (signupError) {
      setLoading(false)
      return setError(signupError.message)
    }

    // Create profile record
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: data.user.id,
        username: form.username.toLowerCase(),
        first_name: form.firstName,
        last_name: form.lastName,
        phone: form.phone,
      })

    setLoading(false)

    if (profileError) {
      if (profileError.message.includes('unique')) {
        return setError('That username is already taken. Try another.')
      }
      return setError(profileError.message)
    }

    onSuccess()
  }

  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <img src='/crossbet-logo.png' alt='Crossbet' style={styles.logoImg} />
        <div style={styles.wordmark}>CROSSBET</div>
        <div style={styles.tagline}>Create your account</div>
      </div>

      <div style={styles.body}>
        <div style={styles.notice}>
          👋 Just the basics — takes 60 seconds
        </div>

        <div style={styles.row}>
          <div style={styles.halfField}>
            <label style={styles.label}>FIRST NAME</label>
            <input style={styles.input} placeholder="Carlos" value={form.firstName} onChange={update('firstName')} />
          </div>
          <div style={styles.halfField}>
            <label style={styles.label}>LAST NAME</label>
            <input style={styles.input} placeholder="Mendez" value={form.lastName} onChange={update('lastName')} />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>EMAIL <span style={styles.hint}>(used to sign in)</span></label>
          <input style={styles.input} type="email" placeholder="your@email.com" value={form.email} onChange={update('email')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>USERNAME <span style={styles.hint}>(your gamertag)</span></label>
          <div style={styles.inputWrapper}>
            <span style={styles.prefix}>@</span>
            <input style={{ ...styles.input, paddingLeft: 28 }} placeholder="cmendez_golf" value={form.username} onChange={update('username')} />
          </div>
          <div style={styles.fieldNote}>How friends find you in the app</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>PHONE <span style={styles.hint}>(required for payments)</span></label>
          <input style={styles.input} type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={update('phone')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>PASSWORD</label>
          <input style={styles.input} type="password" placeholder="Min 8 characters" value={form.password} onChange={update('password')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>CONFIRM PASSWORD</label>
          <input style={styles.input} type="password" placeholder="••••••••" value={form.confirm} onChange={update('confirm')} />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }} onClick={handleSignup} disabled={loading}>
          {loading ? 'Creating account...' : 'Create Account →'}
        </button>

        <div style={styles.loginLink}>
          Already have an account?{' '}
          <span style={styles.link} onClick={onLogin}>Sign in</span>
        </div>
      </div>
    </div>
  )
}

const styles = {
  screen: { minHeight: '100vh', background: '#FDF9E7', display: 'flex', flexDirection: 'column', overflowX: 'hidden', width: '100%' },
  header: { background: '#00563F', padding: '48px 24px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  wordmark: { fontFamily: "'Outfit', sans-serif", fontSize: 36, fontWeight: 800, color: '#FDF9E7', letterSpacing: 6, lineHeight: 1, marginTop: 8 },
  tagline: { fontSize: 12, color: 'rgba(253,249,231,0.5)', marginTop: 8, letterSpacing: 2, textTransform: 'uppercase' },
  body: { flex: 1, padding: '24px 16px 48px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480, width: '100%', margin: '0 auto', boxSizing: 'border-box' },
  notice: { background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#00563F' },
  row: { display: 'flex', gap: 12 },
  halfField: { flex: 1, display: 'flex', flexDirection: 'column' },
  field: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 7 },
  hint: { fontSize: 11, fontWeight: 400, color: '#B0ACA4', letterSpacing: 0, textTransform: 'none' },
  input: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  inputWrapper: { position: 'relative' },
  prefix: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#7A7570', fontWeight: 700, fontSize: 16 },
  fieldNote: { fontSize: 11, color: '#B0ACA4', marginTop: 5 },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#C0392B' },
  btn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3 },
  loginLink: { textAlign: 'center', fontSize: 14, color: '#7A7570' },
  link: { color: '#00563F', fontWeight: 600, cursor: 'pointer' },
}